import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { Box, Button, Collapse, Paper, Typography } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { fetchAnalysisGame, fetchAnalysisHistory, type AnalysisHistoryItem } from "./analysis.api";
import { PRESETS } from "./analysis-presets";
import { useAnalysisStore } from "./analysis.store";
import { useNotification } from "@/app/context/NotificationContext";
import { useMainScrollElement } from "@/app/context/MainScrollContext";
import { AnalysisJobRow } from "./AnalysisProgressPanel";
import { analysisRunner } from "./analysis-runner";
import { useNavigate } from "react-router-dom";
import { gameReviewPath } from "@/features/games/review-navigation";
import { GameMatchLinks } from "@/shared/components/GameMatchLinks";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction } from "@/features/entitlements/entitlement-policy";
import { ApiRequestError } from "@/shared/api/client";
import {
  createRevealAnimationCoordinator,
  requestCoordinatedFinalReveal,
  ScrollCardReveal,
} from "@/shared/components/ScrollCardReveal";

interface AnalysisHistoryListProps {
  onReanalyze: (gameId: string) => void;
}

export const AnalysisHistoryList = ({ onReanalyze }: AnalysisHistoryListProps) => {
  const navigate = useNavigate();
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const mainScrollElement = useMainScrollElement();
  const jobs = useAnalysisStore((state) => state.jobs);
  const isRunning = useAnalysisStore((state) => state.isRunning);
  const { showNotification } = useNotification();
  const [items, setItems] = useState<AnalysisHistoryItem[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const revealAnimationsEnabled = true;
  const loadingRef = useRef(false);
  const queuedRefreshRef = useRef(false);
  const retryingRef = useRef(false);
  const previousRunning = useRef(isRunning);
  const revealedGameIds = useRef(new Set<string>());
  const revealAnimationCoordinator = useRef(createRevealAnimationCoordinator()).current;

  const loadPage = useCallback(async (offset: number, replace = false, queueIfBusy = false) => {
    if (loadingRef.current) {
      if (queueIfBusy) queuedRefreshRef.current = true;
      return undefined;
    }
    loadingRef.current = true;
    setLoading(true);
    setLoadError(false);
    try {
      let currentOffset = offset;
      let shouldReplace = replace;
      while (true) {
        let page: Awaited<ReturnType<typeof fetchAnalysisHistory>>;
        try {
          page = await fetchAnalysisHistory(currentOffset);
        } catch {
          if (!queuedRefreshRef.current) {
            setLoadError(true);
            return undefined;
          }
          queuedRefreshRef.current = false;
          currentOffset = 0;
          shouldReplace = true;
          continue;
        }
        setItems((current) => (shouldReplace ? page.items : [...current, ...page.items]));
        setNextOffset(page.nextOffset);
        if (!queuedRefreshRef.current) return page;
        queuedRefreshRef.current = false;
        currentOffset = 0;
        shouldReplace = true;
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const firstPage = await loadPage(0, true);
      // Warm a second page immediately so fast initial scrolling does not
      // reach the network boundary.
      if (firstPage?.nextOffset != null) {
        await loadPage(firstPage.nextOffset);
      }
    })();
  }, [loadPage]);

  useEffect(() => {
    if (previousRunning.current && !isRunning) {
      setNextOffset(0);
      void loadPage(0, true, true);
      const completedJobs = useAnalysisStore.getState().jobs.filter((job) => job.status === "done");
      if (completedJobs.length === 1) {
        showNotification(`Analysis for ${completedJobs[0].opponent} complete!`);
      } else if (completedJobs.length > 1) {
        showNotification(`${completedJobs.length} game analyses complete!`);
      }
    }
    previousRunning.current = isRunning;
  }, [isRunning, loadPage, showNotification]);

  const jobIds = useMemo(() => new Set(jobs.map((job) => job.gameId)), [jobs]);
  const visibleItems = useMemo(() => items.filter((item) => !jobIds.has(item.gameId)), [items, jobIds]);
  const entries = useMemo(
    () => [
      ...jobs.map((job) => ({ kind: "job" as const, job })),
      ...visibleItems.map((item) => ({ kind: "history" as const, item })),
    ],
    [jobs, visibleItems],
  );
  const deepestPreset = PRESETS[PRESETS.length - 1];

  useEffect(() => {
    if (!mainScrollElement) return;
    let previousTop = mainScrollElement.scrollTop;
    let previousAt = performance.now();
    let settleTimer: number | undefined;
    const trackScrollVelocity = () => {
      const now = performance.now();
      const elapsed = Math.max(1, now - previousAt);
      const currentTop = mainScrollElement.scrollTop;
      const instantaneousVelocity = Math.abs(currentTop - previousTop) / elapsed;
      // Favor the newest sample so a flick compresses the animation promptly,
      // while retaining enough smoothing to avoid jitter between rows.
      revealAnimationCoordinator.scrollVelocity =
        instantaneousVelocity * 0.72 + revealAnimationCoordinator.scrollVelocity * 0.28;
      revealAnimationCoordinator.lastScrollAt = now;
      previousTop = currentTop;
      previousAt = now;
      if (settleTimer != null) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        // A fast-scroll sequence should always conclude with one deliberate,
        // full-weight reveal. The request remains pending while Virtuoso is
        // still rendering scroll-seek placeholders.
        requestCoordinatedFinalReveal(revealAnimationCoordinator);
        revealAnimationCoordinator.scrollVelocity = 0;
      }, 130);
    };
    mainScrollElement.addEventListener("scroll", trackScrollVelocity, { passive: true });
    return () => {
      mainScrollElement.removeEventListener("scroll", trackScrollVelocity);
      if (settleTimer != null) window.clearTimeout(settleTimer);
      if (revealAnimationCoordinator.finalRevealTimer != null) {
        window.clearTimeout(revealAnimationCoordinator.finalRevealTimer);
        revealAnimationCoordinator.finalRevealTimer = undefined;
      }
    };
  }, [mainScrollElement, revealAnimationCoordinator]);

  const retryJob = useCallback(
    async (job: (typeof jobs)[number]) => {
      if (isRunning || retryingRef.current) return;
      retryingRef.current = true;
      setRetrying(true);
      const originPath = window.location.pathname;
      try {
        const game = await fetchAnalysisGame(job.gameId);
        const gate = resolveAnalysisAction(
          entitlementSnapshot,
          job.gameId,
          game.existingAnalysisDepth != null,
          game.exactTimeControl,
        );
        if (!gate.allowed) {
          void navigate(gate.cta === "manage_trial" ? "/subscription#trial" : "/subscription");
          return;
        }
        await analysisRunner.start([game], job.preset);
      } catch (error) {
        if (window.location.pathname === originPath) {
          showNotification(
            error instanceof ApiRequestError ? error.message : `Could not retry analysis for ${job.opponent}.`,
            "error",
          );
        }
      } finally {
        retryingRef.current = false;
        setRetrying(false);
      }
    },
    [entitlementSnapshot, isRunning, navigate, showNotification],
  );

  const toggleExpanded = useCallback((gameId: string) => {
    setExpandedId((current) => (current === gameId ? null : gameId));
  }, []);

  if (entries.length === 0 && nextOffset == null && !loadError && !loading) {
    return (
      <Box sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
          No analyzed games yet. Run analysis to build your engine archive.
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-tour="analysis-history" sx={{ overflowAnchor: "none" }}>
      {loadError && (
        <SnackbarNotice
          severity="error"
          action={
            <Button color="inherit" onClick={() => void loadPage(0, true)}>
              RETRY
            </Button>
          }
          sx={{ mb: 2, borderRadius: 0 }}
        >
          Analysis history did not load. Your saved games are unchanged.
        </SnackbarNotice>
      )}
      <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, mb: 1, display: "block" }}>
        {jobs.length > 0 ? "GAMES" : "ANALYSIS HISTORY"}
      </Typography>
      {mainScrollElement && (
        <Virtuoso
          customScrollParent={mainScrollElement}
          data={entries}
          computeItemKey={(_index, entry) =>
            `${entry.kind}:${entry.kind === "job" ? entry.job.gameId : entry.item.gameId}`
          }
          fixedItemHeight={expandedId == null && jobs.length === 0 ? 74 : undefined}
          defaultItemHeight={74}
          increaseViewportBy={{ top: 140, bottom: 280 }}
          scrollSeekConfiguration={
            expandedId == null
              ? {
                  enter: (velocity) => Math.abs(velocity) > 500,
                  exit: (velocity) => Math.abs(velocity) < 100,
                }
              : false
          }
          endReached={() => {
            if (nextOffset != null) void loadPage(nextOffset);
          }}
          rangeChanged={({ endIndex }) => {
            // Start fetching with roughly twelve rendered records still in
            // reserve instead of waiting until the list reaches its end.
            if (nextOffset != null && endIndex >= entries.length - 12) {
              void loadPage(nextOffset);
            }
          }}
          components={{
            ScrollSeekPlaceholder: ({ index, height }) => (
              <AnalysisRowSkeleton
                height={height}
                active={entries[index]?.kind === "job" && entries[index]?.job.status === "analyzing"}
                job={entries[index]?.kind === "job"}
              />
            ),
            Footer: () =>
              loading ? (
                <HistorySkeletons />
              ) : nextOffset != null ? (
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", py: 2, textAlign: "center", display: "block" }}
                >
                  Scroll for more
                </Typography>
              ) : null,
          }}
          itemContent={(_index, entry) => {
            if (entry.kind === "job") {
              return (
                <ScrollCardReveal
                  scrollRoot={mainScrollElement}
                  revealKey={`job:${entry.job.gameId}`}
                  revealedKeys={revealedGameIds.current}
                  animationsEnabled={revealAnimationsEnabled}
                  revealInitialViewport
                  animationCoordinator={revealAnimationCoordinator}
                >
                  <AnalysisJobRow
                    job={entry.job}
                    isRunning={isRunning || retrying}
                    onRetry={(job) => void retryJob(job)}
                  />
                </ScrollCardReveal>
              );
            }
            const item = entry.item;
            const expanded = expandedId === item.gameId;
            const needsTacticalReanalysis = item.tacticalStatus === "needs_reanalysis";
            const canReanalyze = needsTacticalReanalysis || item.analysisDepth < deepestPreset.depth;
            const firstHistory = visibleItems[0]?.gameId === item.gameId;
            return (
              <ScrollCardReveal
                scrollRoot={mainScrollElement}
                revealKey={`history:${item.gameId}`}
                revealInitialViewport
                revealedKeys={revealedGameIds.current}
                animationsEnabled={revealAnimationsEnabled}
                animationCoordinator={revealAnimationCoordinator}
                topSpacing={firstHistory && jobs.length > 0}
              >
                {firstHistory && jobs.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "primary.main", fontWeight: 700, mb: 1, display: "block" }}
                  >
                    ANALYSIS HISTORY
                  </Typography>
                )}
                <Paper
                  data-lenis-row
                  sx={{
                    mb: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    backgroundImage: "none",
                    contain: "layout paint style",
                  }}
                >
                  <Box
                    onClick={() => toggleExpanded(item.gameId)}
                    data-tour="analysis-history-summary"
                    data-tour-expanded={expanded}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      height: 68,
                      boxSizing: "border-box",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.02)" },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: needsTacticalReanalysis ? "secondary.main" : "primary.main",
                        fontSize: 20,
                        lineHeight: 1,
                      }}
                    >
                      {needsTacticalReanalysis ? "!" : "✓"}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        vs {item.opponent}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        {needsTacticalReanalysis && (
                          <Box
                            component="span"
                            sx={{
                              color: "background.default",
                              bgcolor: "secondary.main",
                              border: "1px solid",
                              borderColor: "secondary.main",
                              px: 0.75,
                              py: 0.25,
                              fontSize: 11,
                            }}
                          >
                            TACTICS MISSING
                          </Box>
                        )}
                        {item.accuracy != null && (
                          <Box
                            component="span"
                            sx={{
                              color: "background.default",
                              bgcolor: "primary.main",
                              border: "1px solid",
                              borderColor: "primary.main",
                              px: 0.75,
                              py: 0.25,
                              fontSize: 11,
                            }}
                          >
                            {item.accuracy.toFixed(0)}% Chess.com
                          </Box>
                        )}
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {item.blunderCount} blunders, {item.mistakeCount} mistakes
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        color: "text.secondary",
                        fontSize: 18,
                        lineHeight: 1,
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "transform 160ms ease-out",
                        willChange: "transform",
                      }}
                    >
                      ▾
                    </Box>
                  </Box>
                  <Collapse in={expanded} timeout={180} unmountOnExit>
                    <Box sx={{ p: 1.5, pt: 1, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
                        <HistoryMetric
                          label="Analysis"
                          value={
                            item.analysisDepth > 0
                              ? `${item.analysisPreset} · depth ${item.analysisDepth}`
                              : "Previous analysis"
                          }
                        />
                        <HistoryMetric label="Est. rating" value={item.estimatedRating ?? "—"} />
                        <HistoryMetric label="Average CPL" value={item.averageCpl.toFixed(1)} />
                        <HistoryMetric label="Inaccuracies" value={item.imprecisionCount} />
                        <HistoryMetric label="Critical positions" value={item.criticalPositionCount} />
                        <HistoryMetric label="Moves analyzed" value={item.movesAnalyzed} />
                        <HistoryMetric label="Positions Calculated" value={item.engineNodes.toLocaleString()} />
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                        {canReanalyze && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={isRunning}
                            onClick={(event) => {
                              event.stopPropagation();
                              onReanalyze(item.gameId);
                            }}
                            sx={{
                              color: needsTacticalReanalysis ? "secondary.main" : "primary.main",
                              borderColor: needsTacticalReanalysis ? "secondary.main" : "primary.main",
                              transition: "none",
                              "&:hover": {
                                color: "background.default",
                                bgcolor: needsTacticalReanalysis ? "secondary.main" : "primary.main",
                                borderColor: needsTacticalReanalysis ? "secondary.main" : "primary.main",
                              },
                            }}
                          >
                            {needsTacticalReanalysis ? "Verify tactics" : "Re-analyze"}
                          </Button>
                        )}
                        <GameMatchLinks
                          reviewHref={gameReviewPath(item.gameId, "review")}
                          chessComUrl={item.chessComUrl}
                        />
                      </Box>
                    </Box>
                  </Collapse>
                </Paper>
              </ScrollCardReveal>
            );
          }}
        />
      )}
    </Box>
  );
};

const HistoryMetric = ({ label, value }: { label: string; value: string | number }) => (
  <Box>
    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const HistorySkeletons = () => (
  <Box aria-label="Loading more analyzed games">
    {[0, 1, 2, 3].map((index) => (
      <Box
        key={index}
        sx={{
          height: 61,
          mb: 1,
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.025), rgba(240,236,226,0.08), rgba(255,255,255,0.025))",
          backgroundSize: "200% 100%",
          animation: "historySkeleton 1.2s linear infinite",
          "@keyframes historySkeleton": {
            from: { backgroundPosition: "200% 0" },
            to: { backgroundPosition: "-200% 0" },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    ))}
  </Box>
);

const AnalysisRowSkeleton = ({ height, active, job }: { height: number; active: boolean; job: boolean }) => (
  <Box sx={{ height, pb: 0.5, boxSizing: "border-box" }}>
    <Paper
      aria-hidden
      sx={{
        height: "100%",
        border: active ? "2px solid" : "1px solid",
        borderColor: active ? "primary.main" : "divider",
        bgcolor: job ? "background.default" : "background.paper",
        backgroundImage: "none",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", gap: 1.5, p: 1.5 }}>
        <Box
          sx={{
            width: 20,
            height: 20,
            flexShrink: 0,
            border: "2px solid",
            borderColor: active ? "primary.main" : "rgba(240,236,226,0.22)",
            borderRadius: active ? "50%" : 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: "clamp(96px, 24%, 180px)",
              height: 8,
              mb: 1,
              bgcolor: active ? "rgba(240,236,226,0.28)" : "rgba(240,236,226,0.18)",
            }}
          />
          <Box sx={{ width: "clamp(110px, 30%, 220px)", height: 6, bgcolor: "rgba(136,136,136,0.2)" }} />
        </Box>
        {!job && (
          <Box
            sx={{
              width: 10,
              height: 10,
              flexShrink: 0,
              borderRight: "2px solid rgba(136,136,136,0.3)",
              borderBottom: "2px solid rgba(136,136,136,0.3)",
              transform: "rotate(45deg)",
            }}
          />
        )}
      </Box>
    </Paper>
  </Box>
);
